@file:Suppress("ktlint:standard:max-line-length")

package com.royceecoleman.bill.shared.billing

import kotlin.math.roundToInt

/**
 * Turns pasted notes and timekeeping exports into [SourceNote]s. Dates and times are only read when they are
 * written unambiguously; anything else stays in the note text and is left blank for the lawyer to complete.
 */
object NoteParsing {
    const val MAX_NOTES = 100

    /** Pasted text: plain lines, `date<TAB>minutes<TAB>note` rows, or a CSV/TSV export with a header row. */
    fun parseText(
        text: String,
        month: String = "",
    ): NoteImportResult {
        val lines = text.lines().filter { it.isNotBlank() }
        if (lines.isEmpty()) return NoteImportResult(warnings = listOf("Add at least one rough note."))
        val separator =
            when {
                lines.first().contains('\t') -> '\t'
                // Pasted CSV needs a recognisable header with a date or time column, so prose with commas stays prose.
                lines.size > 1 && headerColumns(splitDelimited(lines.first(), ','))?.hasTimeOrDate == true -> ','
                else -> null
            }
        if (separator != null) {
            val rows = lines.map { splitDelimited(it, separator) }
            if (headerColumns(rows.first()) != null) return parseRows(rows, month)
            if (separator == '\t') return parseRows(listOf(listOf("date", "minutes", "note")) + rows, month)
        }
        val notes = lines.map { parseLine(it, month) }
        return NoteImportResult(notes, limitWarnings(notes.size))
    }

    /** Spreadsheet rows whose first row is a header naming date, time and description columns. */
    fun parseRows(
        rows: List<List<String>>,
        month: String = "",
    ): NoteImportResult {
        val nonEmpty = rows.filter { row -> row.any { it.isNotBlank() } }
        if (nonEmpty.isEmpty()) return NoteImportResult(warnings = listOf("The file has no rows."))
        val columns =
            headerColumns(nonEmpty.first())
                ?: return NoteImportResult(
                    warnings = listOf("Add a header row with a description column (and optionally date, hours or minutes)."),
                )
        val warnings = mutableListOf<String>()
        val notes =
            nonEmpty.drop(1).mapIndexedNotNull { index, row ->
                val rowNumber = index + 2

                fun cell(column: Int?) = column?.let { row.getOrNull(it) }?.trim().orEmpty()
                val text = cell(columns.note)
                if (text.isBlank()) {
                    warnings += "Row $rowNumber: skipped because the description is empty."
                    return@mapIndexedNotNull null
                }
                val rawDate = cell(columns.date)
                val date = if (rawDate.isBlank()) "" else parseDate(rawDate, month)
                if (rawDate.isNotBlank() && date == null) warnings += "Row $rowNumber: could not read the date \"$rawDate\"."
                val minutes =
                    when {
                        cell(columns.minutes).isNotBlank() -> parseMinutesCell(cell(columns.minutes))
                        cell(columns.hours).isNotBlank() -> parseHoursCell(cell(columns.hours))
                        else -> null
                    }
                val rawTime = cell(columns.minutes).ifBlank { cell(columns.hours) }
                if (rawTime.isNotBlank() && minutes == null) warnings += "Row $rowNumber: could not read the time \"$rawTime\"."
                SourceNote(text = text, date = date.orEmpty(), minutes = minutes, timekeeperId = cell(columns.timekeeper).take(20))
            }
        return NoteImportResult(notes, warnings + limitWarnings(notes.size))
    }

    /** One free-text line, reading a leading date and a leading or trailing duration such as `1.2`, `(0.3)` or `45m`. */
    fun parseLine(
        line: String,
        month: String = "",
    ): SourceNote {
        var rest = line.trim()
        var date = ""
        var minutes: Int? = null
        LEADING_DATE.find(rest)?.let { match ->
            val parsed = parseDate(match.groupValues[1], month)
            if (parsed != null) {
                date = parsed
                rest = rest.substring(match.range.last + 1).trimStart(' ', '-', '–', ':', ',', '\t')
            }
        }
        LEADING_DURATION.find(rest)?.let { match ->
            val parsed = parseDuration(match.groupValues[1])
            if (parsed != null && match.range.last + 1 < rest.length) {
                minutes = parsed
                rest = rest.substring(match.range.last + 1).trimStart(' ', '-', '–', ':', ',', '\t')
            }
        }
        if (minutes == null) {
            // Trailing times need parentheses or a unit so references like "section 3.2" stay text.
            (TRAILING_PAREN_DURATION.find(rest) ?: TRAILING_UNIT_DURATION.find(rest))?.let { match ->
                val parsed = parseDuration(match.groupValues[1])
                if (parsed != null && match.range.first > 0) {
                    minutes = parsed
                    rest = rest.substring(0, match.range.first).trimEnd(' ', '-', '–', ':', ',', ';').removeSuffix(" for").trimEnd()
                }
            }
        }
        return SourceNote(text = rest.ifBlank { line.trim() }, date = date, minutes = minutes)
    }

    /** Returns YYYY-MM-DD for ISO, M/D/YYYY, M/D/YY, M/D or "Sep 4" dates; the billing month supplies a missing year. */
    fun parseDate(
        value: String,
        month: String = "",
    ): String? {
        val trimmed = value.trim().removeSuffix(",")
        val billingYear = month.take(4).toIntOrNull()
        ISO_DATE.matchEntire(trimmed)?.let { return valid(it.groupValues[1].toInt(), it.groupValues[2].toInt(), it.groupValues[3].toInt()) }
        SLASH_DATE.matchEntire(trimmed)?.let {
            val year =
                it.groupValues[3].takeIf(String::isNotEmpty)?.toInt()?.let {
                        y ->
                    if (y < 100) 2000 + y else y
                } ?: billingYear ?: return null
            return valid(year, it.groupValues[1].toInt(), it.groupValues[2].toInt())
        }
        NAMED_DATE.matchEntire(trimmed.lowercase())?.let {
            val monthNumber = MONTHS.indexOf(it.groupValues[1].take(3).lowercase()) + 1
            val year = it.groupValues[3].takeIf(String::isNotEmpty)?.toInt() ?: billingYear ?: return null
            return if (monthNumber == 0) null else valid(year, monthNumber, it.groupValues[2].toInt())
        }
        return null
    }

    /** Minutes for `1.2`, `.5`, `1.5h`, `0.3 hrs`, `45m`, `45 min`, `1:30` or `1h30m`. Bare whole numbers are not durations. */
    fun parseDuration(value: String): Int? {
        val v = value.trim().lowercase()
        COLON_TIME.matchEntire(v)?.let { return checked(it.groupValues[1].toInt() * 60 + it.groupValues[2].toInt()) }
        HOURS_MINUTES.matchEntire(v)?.let { return checked(it.groupValues[1].toInt() * 60 + it.groupValues[2].toInt()) }
        MINUTES.matchEntire(v)?.let { return checked(it.groupValues[1].toInt()) }
        HOURS.matchEntire(v)?.let { match ->
            val number = match.groupValues[1]
            // A bare integer ("2 calls") is not a duration; decimals ("2.0", ".5") or a unit ("2h") are.
            if (!number.contains('.') && match.groupValues[2].isEmpty()) return null
            return number.toDoubleOrNull()?.let { checked((it * 60).roundToInt()) }
        }
        return null
    }

    fun parseHoursCell(value: String): Int? = parseDuration(value) ?: value.trim().toDoubleOrNull()?.let { checked((it * 60).roundToInt()) }

    /** Whole minutes, or a duration with an explicit unit; a bare decimal in a minutes column is ambiguous. */
    fun parseMinutesCell(value: String): Int? =
        value.trim().toIntOrNull()?.let(::checked)
            ?: value.takeIf { v -> v.any { it.isLetter() || it == ':' } }?.let(::parseDuration)

    /** Splits one CSV/TSV line, honouring double-quoted cells with doubled quotes. */
    fun splitDelimited(
        line: String,
        separator: Char,
    ): List<String> {
        val cells = mutableListOf<String>()
        val current = StringBuilder()
        var quoted = false
        var i = 0
        while (i < line.length) {
            val c = line[i]
            when {
                quoted && c == '"' && line.getOrNull(i + 1) == '"' -> {
                    current.append('"')
                    i++
                }
                c == '"' && (quoted || current.isBlank()) -> quoted = !quoted
                c == separator && !quoted -> {
                    cells += current.toString()
                    current.clear()
                }
                else -> current.append(c)
            }
            i++
        }
        cells += current.toString()
        return cells
    }

    /** Splits a whole CSV document into rows, allowing line breaks inside quoted cells. */
    fun splitCsvDocument(
        text: String,
        separator: Char = ',',
    ): List<List<String>> {
        val rows = mutableListOf<String>()
        val current = StringBuilder()
        var quoted = false
        text.replace("\r\n", "\n").replace('\r', '\n').forEach { c ->
            if (c == '"') quoted = !quoted
            if (c == '\n' && !quoted) {
                rows += current.toString()
                current.clear()
            } else {
                current.append(c)
            }
        }
        if (current.isNotEmpty()) rows += current.toString()
        return rows.filter { it.isNotBlank() }.map { splitDelimited(it, separator) }
    }

    private class Columns(
        val date: Int?,
        val hours: Int?,
        val minutes: Int?,
        val note: Int,
        val timekeeper: Int?,
    ) {
        val hasTimeOrDate get() = date != null || hours != null || minutes != null
    }

    private fun headerColumns(header: List<String>): Columns? {
        val names = header.map { cell -> cell.trim().lowercase().split(Regex("[^a-z]+")).filter(String::isNotEmpty) }

        // Keys are tried in priority order; a column matches when it contains the key as a whole word.
        fun find(
            vararg keys: String,
            exclude: Set<String> = emptySet(),
        ): Int? =
            keys.firstNotNullOfOrNull { key ->
                val words = key.split(' ')
                names.indexOfFirst { name -> name.containsAll(words) && name.none { it in exclude } }.takeIf { it >= 0 }
            }
        val note =
            find(
                "description", "narrative", "notes", "note", "details", "detail", "comments", "comment", "task", "work", "activity",
                exclude = setOf("code", "id", "date", "hours", "minutes"),
            ) ?: return null
        return Columns(
            date = find("date", "day"),
            hours = find("hours", "hrs", "hour", "duration", "time", "quantity", "qty", "units", exclude = setOf("minutes", "mins")),
            minutes = find("minutes", "mins", "min"),
            note = note,
            timekeeper = find("timekeeper", "tk", "attorney", "user"),
        )
    }

    private fun limitWarnings(count: Int) =
        if (count > MAX_NOTES) {
            listOf(
                "Found $count notes; a workspace holds at most $MAX_NOTES. Split the month into several workspaces.",
            )
        } else {
            emptyList()
        }

    private fun checked(minutes: Int): Int? = minutes.takeIf { it in 1..1440 }

    private fun valid(
        year: Int,
        month: Int,
        day: Int,
    ): String? {
        if (year !in 1900..2999 || month !in 1..12) return null
        val leap = (year % 4 == 0 && year % 100 != 0) || year % 400 == 0
        val days = intArrayOf(31, if (leap) 29 else 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31)[month - 1]
        if (day !in 1..days) return null
        return "$year-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}"
    }

    private val MONTHS = listOf("jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec")
    private val ISO_DATE = Regex("""(\d{4})-(\d{1,2})-(\d{1,2})""")
    private val SLASH_DATE = Regex("""(\d{1,2})/(\d{1,2})(?:/(\d{2}|\d{4}))?""")
    private val NAMED_DATE = Regex("""([a-z]{3,9})\.? (\d{1,2})(?:,? (\d{4}))?""")
    private val LEADING_DATE =
        Regex(
            """^(\d{4}-\d{1,2}-\d{1,2}|\d{1,2}/\d{1,2}(?:/\d{2,4})?|(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\.? \d{1,2}(?:, \d{4})?)(?=[\s,:\-–]|$)""",
            RegexOption.IGNORE_CASE,
        )
    private const val DURATION = """(?:\d+:\d{2}|\d+h ?\d+m|\d+(?:\.\d+)? ?(?:m|min|mins|minutes)|\d*\.\d+ ?(?:h|hr|hrs|hours?)?|\d+ ?(?:h|hr|hrs|hours?))"""
    private val LEADING_DURATION = Regex("""^($DURATION)(?=[\s,:\-–]|$)""", RegexOption.IGNORE_CASE)
    private const val UNIT_DURATION = """(?:\d+:\d{2}|\d+h ?\d+m|\d+(?:\.\d+)? ?(?:m|min|mins|minutes|h|hr|hrs|hours?)|\.\d+ ?(?:h|hr|hrs|hours?))"""
    private val TRAILING_PAREN_DURATION = Regex("""\(($DURATION)\)\s*$""", RegexOption.IGNORE_CASE)
    private val TRAILING_UNIT_DURATION = Regex("""[\s\-–,;]($UNIT_DURATION)\s*$""", RegexOption.IGNORE_CASE)
    private val COLON_TIME = Regex("""(\d{1,2}):(\d{2})""")
    private val HOURS_MINUTES = Regex("""(\d{1,2})h ?(\d{1,2})m""")
    private val MINUTES = Regex("""(\d{1,4}) ?(?:m|min|mins|minutes)""")
    private val HOURS = Regex("""(\d*\.?\d+) ?(h|hr|hrs|hour|hours)?""")
}
